import React, { useContext, useState } from 'react'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'
import { Trans, t } from '@lingui/macro'
import { ChartType } from 'chart.js'
import { truncate } from 'lodash'

import { TimeUnitEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { AnalyticsHighlight } from '../AnalyticsHighlight'
import Loader from '../../../components/common/loader/Loader'
import { AnalyticsHeader } from '../AnalyticsHeader'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import { graphOptions } from '../chartOptions'
import AnalyticsChart from '../components/AnalyticsChart'
import { Box, Flex, Heading, Link } from 'front-end/src/components'
import { navigateTo } from '../../../app/util/appHistory'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { MediaDetailOverviewTable } from './MediaDetailOverviewTable'
import { MediaEngagementChart } from './MediaEngagementChart'
import { useMediaDetailAnalyticsQuery } from './MediaDetailAnalytics.codegen'
import { formatDateAndTimeShort } from '../../../utils/dateHelpers'
import { MediaElement } from '../../topic-detail-page/components/video-element/components/MediaElement'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import { AnalyticsContext } from '../AnalyticsContext'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'
import { AnalyticsFiltersEnum } from '../constants'

export const MediaDetailAnalytics = () => {
  const history = useHistory()
  const params = useParams<{ topicElementId: string }>()
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const [chartTimeUnit, setChartTimeUnit] = useState<TimeUnitEnum>(
    TimeUnitEnum.DAY
  )
  const topicElementId = Number(params.topicElementId)

  const { data, loading } = useMediaDetailAnalyticsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      mainAnalyticsFilter: { ...filterAsQueryVariables, topicElementId },
      topicElementId
    }
  })

  if (loading || !data?.analytics.media) {
    return <Loader />
  }

  const media = data.analytics.media
  const filename = media.topicElement?.mediaFileName || ''

  const renderAnalyticsBoxes = () => {
    return (
      <>
        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <div className="AnalyticsHighlight__row">
              <AnalyticsHighlight
                value={`${media.averageEngagement}%`}
                icon="rocket"
                description={t`Avg engagement`}
                iconColor="grey"
              />
              <AnalyticsHighlight
                value={media.playsCount}
                icon="control-play"
                description={t`Plays`}
                iconColor="green"
              />
              <AnalyticsHighlight
                value={media.impressionsCount}
                icon="eye"
                description={t`Impressions`}
                iconColor="blue"
              />
              <AnalyticsHighlight
                value={`${media.playRate}%`}
                icon="pie-chart"
                description={t`Play rate`}
                iconColor="grey"
              />
              <AnalyticsHighlight
                value={
                  media.playTimeMinutes < 60
                    ? t`${media.playTimeMinutes}m`
                    : t`${(media.playTimeMinutes / 60).toFixed(1)}h`
                }
                icon="clock"
                description={t`Play time`}
                iconColor="green"
              />
            </div>
          </BorderedBox>
        </div>

        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <Box px="20px" py="28px">
              <Flex alignItems="flex-start">
                <Box maxW="426px" w="100%">
                  <MediaElement
                    {...(data.analytics.media.topicElement as any)}
                    isVisible={true}
                    shouldTrackMedia={false}
                    topicId={
                      data.analytics.media.topicElement?.topicVersion.topic.id
                    }
                  />
                </Box>
                <Box ml="32px" flex={1}>
                  <Heading size="md" mb="12px">
                    <Trans>Engagement</Trans>
                  </Heading>
                  <MediaEngagementChart topicElementId={topicElementId} />
                </Box>
              </Flex>
            </Box>
          </BorderedBox>
        </div>

        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <Heading pl="20px" pt="28px" size="md">
              <Trans>Play</Trans>
            </Heading>
            <AnalyticsChart
              width={800}
              height={160}
              config={{
                type: 'line' as ChartType,
                options: graphOptions
              }}
              data={[
                {
                  dataset: media.impressionsByTimeUnit,
                  color: '11,146,213',
                  tooltipLabel: t`Impressions`
                },
                {
                  dataset: media.onlyEngagementsByTimeUnit,
                  tooltipLabel: t`Plays`
                }
              ]}
              onGranularityChanged={setChartTimeUnit}
              granularity={chartTimeUnit}
            />
          </BorderedBox>
        </div>

        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <Heading pl="20px" pt="28px" size="md">
              <Trans>Details</Trans>
            </Heading>

            <Box pl="20px" mt="16px" fontSize="lg">
              <Box>
                <Trans>Uploaded at</Trans>:{' '}
                {formatDateAndTimeShort(media.topicElement?.created_at)}
              </Box>
              <Box>
                <Trans>Uploaded to </Trans>{' '}
                <Link
                  onClick={() =>
                    media.topicElement &&
                    navigateTo(
                      linkBuilder.topicUrl(
                        media.topicElement.topicVersion.topic.id
                      ),
                      true
                    )
                  }
                >
                  <strong>
                    {media.topicElement?.topicVersion.topic.currentTitle}
                  </strong>
                  {media.topicElement?.topicVersion.topic.deleted_at && (
                    <StatusArchivedGrey />
                  )}
                </Link>
              </Box>
              <Box>
                <Link
                  onClick={() =>
                    media.topicElement &&
                    navigateTo(
                      linkBuilder.analyticsResourceDetail(
                        media.topicElement.topicVersion.topic.id
                      ),
                      true
                    )
                  }
                >
                  <Trans>View resource analytics</Trans>
                </Link>
              </Box>
            </Box>
          </BorderedBox>

          <BorderedBox className="AnalyticsRow__box">
            <Heading pl="20px" pt="28px" size="md">
              <Trans>Unique</Trans>
            </Heading>
            <div className="AnalyticsHighlight__row AnalyticsHighlight__row--videos">
              <AnalyticsHighlight
                value={media.uniquePlaysCount}
                icon="control-play"
                iconColor="green"
                description={t`Unique plays`}
              />

              <AnalyticsHighlight
                value={`${media.uniqueImpressionsCount}`}
                icon="eye"
                iconColor="blue"
                description={t`Unique impressions`}
              />

              <AnalyticsHighlight
                value={`${media.uniquePlayRate}%`}
                icon="pie-chart"
                iconColor="grey"
                description={t`Unique play rate`}
              />
            </div>
          </BorderedBox>
        </div>
      </>
    )
  }

  return (
    <>
      <AnalyticsHeader
        showFilterPanel
        goBack={history.goBack}
        activeFilters={[
          AnalyticsFiltersEnum.INCLUDE_ADMINS,
          AnalyticsFiltersEnum.INCLUDE_ARCHIVED,
          AnalyticsFiltersEnum.DATE_FILTER
        ]}
      >
        <>
          {filename?.length >= 40 ? (
            <Tooltip text={filename}>
              {truncate(filename || '', { length: 40 })}
            </Tooltip>
          ) : (
            filename
          )}
          {!!media.topicElement?.deleted_at && <StatusArchivedGrey mx="8px" />}
        </>
      </AnalyticsHeader>

      {renderAnalyticsBoxes()}

      <MediaDetailOverviewTable topicElementId={topicElementId} />
    </>
  )
}
